'use client';

import { c as _c } from "react/compiler-runtime";
import { SignIn, useAuth as useClerkAuth } from '@clerk/nextjs';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { useAuth as useFirebaseAuth } from '@/context/AuthContext';
const redirectPath = '/dashboard';

// Using our custom sign-in component:

export default function FirebaseAuth() {
  const $ = _c(27);
  const router = useRouter();
  const {
    userId: clerkUserId,
    isLoaded: clerkIsLoaded,
    isSignedIn: clerkIsSignedIn
  } = useClerkAuth();
  const {
    firebaseUser,
    login,
    isLoggingIn,
    isLoggingOut,
    tokenVerified,
    isAuthReady
  } = useFirebaseAuth();
  const [showRedirectMessage, setShowRedirectMessage] = useState(false);
  const readyToRedirect = isAuthReady && firebaseUser && clerkUserId && !isLoggingIn && !isLoggingOut && tokenVerified;
  let t0;
  let t1;
  if ($[0] !== readyToRedirect || $[1] !== router) {
    t0 = () => {
      if (readyToRedirect) {
        console.log("[auth] User fully authenticated, redirecting to dashboard");
        const redirectButtonTimer = setTimeout(() => setShowRedirectMessage(true), 3000);
        router.replace(redirectPath);
        return () => clearTimeout(redirectButtonTimer);
      } else {
        setShowRedirectMessage(false);
      }
    };
    t1 = [readyToRedirect, router];
    $[0] = readyToRedirect;
    $[1] = router;
    $[2] = t0;
    $[3] = t1;
  } else {
    t0 = $[2];
    t1 = $[3];
  }
  useEffect(t0, t1);
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      backgroundImage: "url(/genesys-bg.jpg)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed"
    };
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const authLoadingBackdropStyle = t2 as const;
  if (clerkIsLoaded && !clerkUserId) {
    let t3;
    if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <DefaultClerkSignIn />;
      $[5] = t3;
    } else {
      t3 = $[5];
    }
    return t3;
  }
  if (!isAuthReady) {
    let t3;
    if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="absolute inset-0 bg-black/30" />;
      $[6] = t3;
    } else {
      t3 = $[6];
    }
    let t4;
    if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="fixed inset-0 flex items-center justify-center" style={authLoadingBackdropStyle}>{t3}<div className="relative z-10 flex flex-col items-center gap-2"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4" /><p className="text-white text-base font-medium">Initializing authentication...</p></div></div>;
      $[7] = t4;
    } else {
      t4 = $[7];
    }
    return t4;
  }
  if (isLoggingIn || isLoggingOut) {
    console.log("[auth] Loading:", {
      isLoggingIn,
      isLoggingOut
    });
    let t3;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = <div className="absolute inset-0 bg-black/30" />;
      $[8] = t3;
    } else {
      t3 = $[8];
    }
    let t4;
    if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4" />;
      $[9] = t4;
    } else {
      t4 = $[9];
    }
    const t5 = isLoggingIn ? "Logging in..." : "Logging out...";
    let t6;
    if ($[10] !== t5) {
      t6 = <div className="fixed inset-0 flex items-center justify-center" style={authLoadingBackdropStyle}>{t3}<div className="relative z-10 flex flex-col items-center gap-2">{t4}<p className="text-white text-base font-medium">{t5}</p></div></div>;
      $[10] = t5;
      $[11] = t6;
    } else {
      t6 = $[11];
    }
    return t6;
  }
  if (!firebaseUser) {
    let t3;
    let t4;
    if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
      t3 = {
        backgroundImage: "url(/genesys-bg.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        backgroundAttachment: "fixed"
      };
      t4 = <div className="absolute inset-0 bg-black/30" />;
      $[12] = t3;
      $[13] = t4;
    } else {
      t3 = $[12];
      t4 = $[13];
    }
    let t5;
    let t6;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t5 = <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-4" />;
      t6 = <p className="text-white text-base font-medium mb-4">Restoring session...</p>;
      $[14] = t5;
      $[15] = t6;
    } else {
      t5 = $[14];
      t6 = $[15];
    }
    let t7;
    if ($[16] !== login) {
      t7 = <div className="fixed inset-0 flex items-center justify-center" style={t3}>{t4}<div className="relative z-10 flex flex-col items-center justify-center p-4">{t5}{t6}<button onClick={login} className="rounded-full border border-solid border-white/20 transition-colors flex items-center justify-center bg-white/10 text-white gap-2 hover:bg-white/20 font-medium text-sm sm:text-base h-10 sm:h-12 px-4 sm:px-5 sm:w-auto">Continue</button></div></div>;
      $[16] = login;
      $[17] = t7;
    } else {
      t7 = $[17];
    }
    return t7;
  }
  console.log("[auth] Waiting to redirect:", {
    firebaseUser,
    clerkUserId,
    isLoggingIn,
    isLoggingOut,
    clerkIsSignedIn,
    tokenVerified
  });
  let t3;
  let t4;
  if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      backgroundImage: "url(/genesys-bg.jpg)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed"
    };
    t4 = <div className="absolute inset-0 bg-black/30" />;
    $[18] = t3;
    $[19] = t4;
  } else {
    t3 = $[18];
    t4 = $[19];
  }
  let t5;
  let t6;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-white mb-2" />;
    t6 = <p className="text-white text-base font-medium">Loading...</p>;
    $[20] = t5;
    $[21] = t6;
  } else {
    t5 = $[20];
    t6 = $[21];
  }
  let t7;
  if ($[22] !== router || $[23] !== showRedirectMessage) {
    t7 = showRedirectMessage && <Button onClick={() => router.replace("/dashboard")} variant="clear">If you are not redirected after 5 seconds, click here to go to dashboard</Button>;
    $[22] = router;
    $[23] = showRedirectMessage;
    $[24] = t7;
  } else {
    t7 = $[24];
  }
  let t8;
  if ($[25] !== t7) {
    t8 = <div className="fixed inset-0 flex items-center justify-center" style={t3}>{t4}<div className="relative z-10 flex flex-col items-center gap-4">{t5}{t6}{t7}</div></div>;
    $[25] = t7;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  return t8;
}
export const DefaultClerkSignIn = () => {
  const $ = _c(2);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      backgroundImage: "url(/genesys-bg.jpg)",
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      backgroundAttachment: "fixed"
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <div className="fixed inset-0 flex items-center justify-center" style={t0}><div className="relative z-10 flex flex-col items-center gap-6 sm:gap-8 lg:gap-10 2xl:gap-[2.22vh] px-4 py-8 sm:px-0 sm:py-0 max-sm:landscape:gap-3 max-sm:landscape:py-4"><div className="flex justify-center items-center shrink-0 opacity-70 w-40 h-6 sm:w-48 sm:h-7 md:w-56 md:h-8 lg:w-64 lg:h-10 xl:w-72 xl:h-12 2xl:w-[15.52vw] 2xl:h-[2.5vh] max-sm:landscape:w-32 max-sm:landscape:h-5"><Image src="/Logo Genesys.svg" alt="Genesys" width={299} height={27} className="w-full h-full object-contain" /></div><SignIn forceRedirectUrl={redirectPath} /></div></div>;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  return t1;
};